import { useState, useCallback, useMemo, useEffect } from 'react';
import PageTitle from '../components/PageTitle';
import { Settings, Search, ChevronLeft, ChevronRight, AlertCircle, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useAppAuth } from '@/hooks/useAppAuth';
import { useTenantUsers, useAvailableRoles, useUpdateUserRoles } from '@/hooks/useTenantUsers';
import { useDimensionsList } from '@/hooks/useDimension';
import { useMeasures } from '@/hooks/useMeasures';
import { useUserSettings, useUpdateUserSettings } from '@/hooks/useUserSettings';
import { DimensionFilterTable } from '@/components/DimensionFilterTable';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { AnalysisDimensionFilter } from '@/utils/filterComparators';
import type { TenantUser, FronteggRole } from '@/services/user.service';
import { Badge } from '@/components/ui/badge';
import type { ComboboxOption } from '@/components/ui/combobox';
import { toast } from 'sonner';
import { useCustomPeriodVariants } from '@/hooks/useDateDimensionCustomPeriods';

function UserPermissionsDialog({
  user,
  open,
  onOpenChange,
}: {
  user: TenantUser;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: savedSettings, isLoading: settingsLoading } =
    useUserSettings(user.id, { enabled: open });
  const { data: dimensionsData } = useDimensionsList({
    pageSize: 500,
    enabled: open,
  });
  const { data: measuresData, isLoading: measuresLoading } = useMeasures({
    pageSize: 500,
    enabled: open,
  });
  const { data: variants } = useCustomPeriodVariants();
  const { data: availableRoles } = useAvailableRoles();
  const updateSettingsMutation = useUpdateUserSettings();
  const updateRolesMutation = useUpdateUserRoles();

  const [localFilters, setLocalFilters] = useState<AnalysisDimensionFilter[] | null>(null);
  const [localBlacklistedIds, setLocalBlacklistedIds] = useState<string[] | null>(null);
  const [localVariant, setLocalVariant] = useState<string | null | undefined>(undefined);
  const [localRoleIds, setLocalRoleIds] = useState<string[] | null>(null);

  const filters = localFilters ?? savedSettings?.dimension_filters ?? [];
  const blacklistedIds = localBlacklistedIds ?? savedSettings?.blacklisted_metric_ids ?? [];
  const variant = localVariant !== undefined ? localVariant : (savedSettings?.date_dimension_variant ?? null);

  const currentRoleIds = useMemo(() => (user.roles || []).map(r => r.id), [user.roles]);
  const selectedRoleIds = localRoleIds ?? currentRoleIds;

  // Reset local state when settings load
  useEffect(() => {
    if (open) {
      setLocalFilters(null);
      setLocalBlacklistedIds(null);
      setLocalVariant(undefined);
      setLocalRoleIds(null);
    }
  }, [open]);

  const dimensionOptions: ComboboxOption[] = useMemo(() => {
    if (!dimensionsData?.records) return [];
    return dimensionsData.records.map((d) => ({
      value: d.id,
      label: d.name,
    }));
  }, [dimensionsData]);

  const handleFiltersChange = useCallback(
    (newFilters: AnalysisDimensionFilter[]) => {
      setLocalFilters(newFilters);
    },
    []
  );

  const handleMetricToggle = useCallback(
    (metricId: string, checked: boolean) => {
      setLocalBlacklistedIds((prev) => {
        const current = prev ?? savedSettings?.blacklisted_metric_ids ?? [];
        if (checked) {
          return current.filter((id) => id !== metricId);
        } else {
          return [...current, metricId];
        }
      });
    },
    [savedSettings?.blacklisted_metric_ids]
  );

  const handleRoleToggle = useCallback((roleId: string, checked: boolean) => {
    setLocalRoleIds(prev => {
      const current = prev ?? currentRoleIds;
      if (checked) {
        return [...current, roleId];
      } else {
        return current.filter(id => id !== roleId);
      }
    });
  }, [currentRoleIds]);

  const handleSave = useCallback(async () => {
    try {
      const promises: Promise<any>[] = [];
      promises.push(updateSettingsMutation.mutateAsync({
        userFronteggId: user.id,
        settings: {
          dimension_filters: filters,
          blacklisted_metric_ids: blacklistedIds,
          date_dimension_variant: variant,
        },
      }));
      if (localRoleIds !== null) {
        promises.push(updateRolesMutation.mutateAsync({
          userId: user.id,
          roleIds: localRoleIds,
        }));
      }
      await Promise.all(promises);
      setLocalFilters(null);
      setLocalBlacklistedIds(null);
      setLocalVariant(undefined);
      setLocalRoleIds(null);
      toast.success('User settings saved');
      onOpenChange(false);
    } catch {
      toast.error('Failed to save user settings');
    }
  }, [filters, blacklistedIds, variant, localRoleIds, user.id, updateSettingsMutation, updateRolesMutation, onOpenChange]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setLocalFilters(null);
        setLocalBlacklistedIds(null);
        setLocalVariant(undefined);
        setLocalRoleIds(null);
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange]
  );

  const isSaving = updateSettingsMutation.isPending || updateRolesMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>
            Settings for {user.name || user.email}
            {user.name && user.email && (
              <span className="text-muted-foreground font-normal text-sm ml-2">{user.email}</span>
            )}
          </DialogTitle>
          <DialogDescription>
            Manage data filters, metric visibility, and other settings for this user.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="py-4 overflow-y-auto min-h-0">
          {settingsLoading ? (
            <div className="py-8 text-center text-muted-foreground">Loading...</div>
          ) : (
            <Tabs defaultValue="dimensions">
              <TabsList>
                <TabsTrigger value="dimensions">Dimension Filters</TabsTrigger>
                <TabsTrigger value="metrics">Metric Filters</TabsTrigger>
                <TabsTrigger value="roles">Roles</TabsTrigger>
                <TabsTrigger value="other">Other Settings</TabsTrigger>
              </TabsList>
              <TabsContent value="roles">
                <div className="space-y-3 py-2">
                  <p className="text-sm text-muted-foreground">
                    Select the roles for this user. Changes take effect after saving.
                  </p>
                  {!availableRoles?.length ? (
                    <div className="py-4 text-center text-muted-foreground">Loading roles...</div>
                  ) : (
                    <div className="space-y-2">
                      {availableRoles.map((role) => (
                        <div key={role.id} className="flex items-center gap-2">
                          <Checkbox
                            id={`role-${role.id}`}
                            checked={selectedRoleIds.includes(role.id)}
                            onCheckedChange={(checked) => handleRoleToggle(role.id, !!checked)}
                          />
                          <Label htmlFor={`role-${role.id}`} className="font-normal">
                            {role.name}
                          </Label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
              <TabsContent value="dimensions">
                <DimensionFilterTable
                  dimensionFilters={filters}
                  onChange={handleFiltersChange}
                  dimensionOptions={dimensionOptions}
                />
              </TabsContent>
              <TabsContent value="metrics">
                {measuresLoading ? (
                  <div className="py-8 text-center text-muted-foreground">Loading...</div>
                ) : !measuresData?.records?.length ? (
                  <div className="py-8 text-center text-muted-foreground">No metrics configured.</div>
                ) : (
                  <div className="max-h-[400px] overflow-y-auto border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]">Visible</TableHead>
                          <TableHead>Metric</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {measuresData.records.map((measure) => {
                          const isAllowed = !blacklistedIds.includes(measure.id);
                          return (
                            <TableRow key={measure.id}>
                              <TableCell>
                                <Checkbox
                                  checked={isAllowed}
                                  onCheckedChange={(checked) =>
                                    handleMetricToggle(measure.id, !!checked)
                                  }
                                />
                              </TableCell>
                              <TableCell>{measure.name}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </TabsContent>
              <TabsContent value="other">
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label htmlFor="variant">Date Dimension Variant</Label>
                    <p className="text-xs text-muted-foreground">
                      When set, this user will see date dimension data from the selected variant.
                    </p>
                    <Select
                      value={variant ?? '__none__'}
                      onValueChange={(value) => setLocalVariant(value === '__none__' ? null : value)}
                    >
                      <SelectTrigger id="variant" className="w-[300px]">
                        <SelectValue placeholder="Default (no variant)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Default (no variant)</SelectItem>
                        {variants?.map((v) => (
                          <SelectItem key={v} value={v}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const PAGE_SIZE = 20;

export default function UsersPage() {
  const { user: currentUser } = useAppAuth();
  const { data: users, isLoading, isError, refetch } = useTenantUsers();
  const [selectedUser, setSelectedUser] = useState<TenantUser | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const handleOpenSettings = useCallback((user: TenantUser) => {
    setSelectedUser(user);
    setDialogOpen(true);
  }, []);

  const filteredUsers = useMemo(() => {
    if (!users) return [];
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q)
    );
  }, [users, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredUsers.slice(start, start + PAGE_SIZE);
  }, [filteredUsers, currentPage]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Check if the current user has Admin role
  const isAdmin = useMemo(() => {
    if (!currentUser) return false;
    const roles = currentUser.roles || [];
    return roles.some(
      (r: any) => (typeof r === 'string' ? r : r?.key || r?.name || '').toLowerCase() === 'admin'
    );
  }, [currentUser]);

  if (!isAdmin) {
    return (
      <div className="p-8">
        <PageTitle title="User Permissions" className="mb-4" />
        <p className="text-muted-foreground">You need Admin access to manage user permissions.</p>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl">
      <div className="mb-6">
        <PageTitle title="User Permissions" />
        <p className="text-muted-foreground mt-1">
          Manage data-level dimension filters for each user. Filters are automatically applied to all queries.
        </p>
      </div>

      {isError ? (
        <div className="flex items-center gap-3 rounded-md border border-destructive/50 bg-destructive/10 p-4">
          <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-destructive">Failed to load users</p>
            <p className="text-sm text-muted-foreground">Something went wrong fetching the user list. Please try again.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 max-w-sm"
            />
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead className="w-[80px] text-right">Settings</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedUsers.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">{u.name || '-'}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(u.roles || []).map(r => (
                        <Badge key={r.id} variant="secondary">{r.name}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => handleOpenSettings(u)}
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {paginatedUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    {searchQuery ? 'No users match your search.' : 'No users found.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-muted-foreground">
                {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
                {searchQuery && users ? ` (of ${users.length})` : ''}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {selectedUser && (
        <UserPermissionsDialog
          user={selectedUser}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      )}
    </div>
  );
}
